import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

function ClientCard({ client, index }) {
  const hasLogo = !!client.logo;

  return (
    <div
      data-testid={`client-${index}`}
      className="flex-shrink-0 flex flex-col items-center justify-center px-8 py-6 md:px-10 md:py-7 border border-slate-800/60 hover:border-[#D4AF37]/30 bg-[#0F1A2E] hover:bg-[#162A45] transition-all duration-500 group cursor-default relative overflow-hidden min-w-[180px] md:min-w-[220px]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/[0.03] group-hover:to-transparent transition-all duration-500 pointer-events-none" />
      <div className="relative z-10 flex flex-col items-center">
        {hasLogo ? (
          <div className="h-10 w-full flex items-center justify-center mb-3">
            <img
              src={client.logo}
              alt={`${client.name} logo`}
              className="max-h-10 max-w-[140px] object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
              loading="lazy"
              style={client.keepColor ? {} : { filter: "brightness(0) invert(1)" }}
            />
          </div>
        ) : (
          <div
            className="text-slate-300 group-hover:text-white text-sm font-semibold text-center transition-colors duration-300 leading-snug whitespace-nowrap tracking-wide uppercase mb-1"
            style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.15em" }}
          >
            {client.name}
          </div>
        )}
        <div className="text-[#D4AF37]/25 group-hover:text-[#D4AF37]/60 text-[10px] uppercase tracking-widest mt-2 transition-colors duration-300 text-center whitespace-nowrap">
          {client.sector}
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [ref, isVisible] = useScrollReveal();
  const { t } = useLanguage();
  const c = t.clients;

  const row1 = c.items.slice(0, 7);
  const row2 = c.items.slice(7);

  return (
    <section id="clients" data-testid="clients-section" ref={ref} className="bg-[#1E3455] py-24 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.02] rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14 transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(16px)" }}>
          <span className="text-slate-300 text-xs uppercase tracking-widest">{c.header}</span>
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mt-3" />
        </div>
      </div>

      {/* Marquee row 1 */}
      <div className="relative mb-4 overflow-hidden" style={{ opacity: isVisible ? 1 : 0, transition: "opacity 0.8s ease-out", transitionDelay: "200ms" }}>
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#1E3455] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#1E3455] to-transparent z-10 pointer-events-none" />
        <div className="marquee-left flex gap-4 w-max">
          {[...row1, ...row1].map((client, i) => (
            <ClientCard key={`r1-${i}`} client={client} index={i % row1.length} />
          ))}
        </div>
      </div>

      {/* Marquee row 2 */}
      <div className="relative overflow-hidden" style={{ opacity: isVisible ? 1 : 0, transition: "opacity 0.8s ease-out", transitionDelay: "400ms" }}>
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#1E3455] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#1E3455] to-transparent z-10 pointer-events-none" />
        <div className="marquee-right flex gap-4 w-max">
          {[...row2, ...row2].map((client, i) => (
            <ClientCard key={`r2-${i}`} client={client} index={(i % row2.length) + row1.length} />
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-wrap justify-center gap-3 mt-12 transition-all duration-700" style={{ opacity: isVisible ? 1 : 0, transitionDelay: "600ms" }}>
          {c.sectors.map((sector) => (
            <span key={sector} className="border border-slate-800/60 text-slate-300 text-[11px] uppercase tracking-widest px-4 py-2 hover:border-[#D4AF37]/20 hover:text-slate-300 transition-all duration-300">
              {sector}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
